type ApiErrorShape = {
  status?: number;
  error?:
    | string
    | {
        message?: string | string[];
        error?: Record<string, string[] | string>;
      };
  message?: string;
};

function extractFieldValidationMessages(nested: unknown): string | null {
  if (!nested || typeof nested !== 'object' || Array.isArray(nested)) {
    return null;
  }
  const fieldMessages = Object.entries(nested as Record<string, string[] | string>)
    .flatMap(([, value]) => (Array.isArray(value) ? value : [value]))
    .filter((m): m is string => typeof m === 'string' && m.trim().length > 0)
    .map(m => m.trim());
  return fieldMessages.length > 0 ? fieldMessages.join(' ') : null;
}

function readHttpErrorBodyMessage(body: NonNullable<ApiErrorShape['error']>): string | null {
  if (typeof body === 'string' && body.trim()) {
    return body.trim();
  }
  if (!body || typeof body !== 'object') {
    return null;
  }

  const nested = body.error;
  const fromFields = extractFieldValidationMessages(nested);
  if (fromFields) {
    return fromFields;
  }

  const bodyMessage = body.message;
  if (Array.isArray(bodyMessage) && bodyMessage.length > 0) {
    return bodyMessage.join(', ');
  }
  if (typeof bodyMessage !== 'string' || !bodyMessage.trim()) {
    return null;
  }

  if (bodyMessage.trim().toLowerCase() === 'validation failed') {
    const validationFields = extractFieldValidationMessages(nested);
    if (validationFields) {
      return validationFields;
    }
  }
  return bodyMessage.trim();
}

function isAngularHttpFailureMessage(message: string): boolean {
  return /^Http failure response for /i.test(message.trim());
}

export function readApiErrorMessage(error: unknown, fallback = ''): string {
  if (error instanceof Error && (error as Error & { cause?: unknown }).cause) {
    return readApiErrorMessage((error as Error & { cause?: unknown }).cause, fallback);
  }

  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const err = error as ApiErrorShape;

  if (err.status === 0) {
    return 'Unable to reach the server. Check your connection and try again.';
  }

  const body = err.error;
  if (body !== undefined && body !== null) {
    const bodyMessage = readHttpErrorBodyMessage(body);
    if (bodyMessage) {
      return bodyMessage;
    }
  }

  if (typeof err.message === 'string' && err.message.trim() && !isAngularHttpFailureMessage(err.message)) {
    return err.message.trim();
  }

  return fallback;
}

export function readApiEnvelopeMessage(
  response: { status?: boolean; message?: string } | null | undefined,
  fallback = "Something went wrong. Please try again.",
): string {
  if (response?.status === false) {
    return response.message?.trim() || fallback;
  }
  return fallback;
}
